import type { Goal, Task, SubTask, ChecklistItem } from '../../types/timeline'
import type { ChecklistDragPayload, ChecklistDropTarget, DetailedSelection } from './types'

/** Locate a checklist by id within the goal tree. Returns its container path. */
const findChecklistLocation = (
  goal: Goal,
  checklistId: string,
): { goalId: string; taskId: string | null; subTaskId: string | null; index: number; item: ChecklistItem } | null => {
  const inGoal = goal.checklist.findIndex((item) => item.id === checklistId)
  if (inGoal >= 0) {
    return { goalId: goal.id, taskId: null, subTaskId: null, index: inGoal, item: goal.checklist[inGoal] }
  }
  for (const task of goal.tasks) {
    const inTask = task.checklist.findIndex((item) => item.id === checklistId)
    if (inTask >= 0) {
      return { goalId: goal.id, taskId: task.id, subTaskId: null, index: inTask, item: task.checklist[inTask] }
    }
    for (const subTask of task.subTasks) {
      const inSub = subTask.checklist.findIndex((item) => item.id === checklistId)
      if (inSub >= 0) {
        return { goalId: goal.id, taskId: task.id, subTaskId: subTask.id, index: inSub, item: subTask.checklist[inSub] }
      }
    }
  }
  return null
}

/** Build a drag payload from a checklist's tree position. */
export const buildDragPayload = (
  goal: Goal,
  selection: { goalId: string; taskId?: string | null; subTaskId?: string | null; checklistId: string },
): ChecklistDragPayload | null => {
  const location = findChecklistLocation(goal, selection.checklistId)
  if (!location) return null
  return {
    checklistId: location.item.id,
    fromGoalId: location.goalId,
    fromTaskId: location.taskId,
    fromSubTaskId: location.subTaskId,
    fromIndex: location.index,
  }
}

/** Helper: pure-update checklist array on a container. */
const replaceContainerChecklist = (
  goal: Goal,
  taskId: string | null,
  subTaskId: string | null,
  updater: (current: ChecklistItem[]) => ChecklistItem[],
): Goal => {
  if (!taskId) {
    return { ...goal, checklist: updater(goal.checklist) }
  }
  return {
    ...goal,
    tasks: goal.tasks.map((task) => {
      if (task.id !== taskId) return task
      if (!subTaskId) {
        return { ...task, checklist: updater(task.checklist) }
      }
      return {
        ...task,
        subTasks: task.subTasks.map((sub) =>
          sub.id === subTaskId ? { ...sub, checklist: updater(sub.checklist) } : sub,
        ),
      }
    }),
  }
}

/**
 * Validate a drop target against a drag payload. The Detailed View only ever
 * holds a single goal, so cross-goal drops would only happen if the two ids
 * diverge — they shouldn't, but we guard anyway.
 */
export const isValidDrop = (payload: ChecklistDragPayload, target: ChecklistDropTarget): boolean => {
  if (target.kind === 'into-goal') return target.goalId === payload.fromGoalId
  if (target.kind === 'into-task') return target.goalId === payload.fromGoalId
  if (target.kind === 'into-subtask') return target.goalId === payload.fromGoalId
  if (target.kind === 'before-checklist') {
    // Can't drop onto itself (no-op move).
    if (target.checklistId === payload.checklistId) return false
    return target.goalId === payload.fromGoalId
  }
  return false
}

/**
 * Move a checklist within the goal tree given a validated payload + target.
 * Returns the next goal and the new selection pointing at the moved item so
 * the UI keeps focus on it after the drop.
 */
export const applyChecklistMove = (
  goal: Goal,
  payload: ChecklistDragPayload,
  target: ChecklistDropTarget,
): { next: Goal; nextSelection: DetailedSelection } | null => {
  const location = findChecklistLocation(goal, payload.checklistId)
  if (!location) return null
  const movedItem = location.item

  // Step 1 — remove from source.
  let stagedGoal = replaceContainerChecklist(goal, location.taskId, location.subTaskId, (current) =>
    current.filter((item) => item.id !== payload.checklistId),
  )

  // Step 2 — insert at target.
  let nextSelection: DetailedSelection
  if (target.kind === 'into-goal') {
    stagedGoal = replaceContainerChecklist(stagedGoal, null, null, (current) => [...current, movedItem])
    nextSelection = {
      kind: 'checklist',
      goalId: stagedGoal.id,
      taskId: null,
      subTaskId: null,
      checklistId: movedItem.id,
    }
  } else if (target.kind === 'into-task') {
    stagedGoal = replaceContainerChecklist(stagedGoal, target.taskId, null, (current) => [...current, movedItem])
    nextSelection = {
      kind: 'checklist',
      goalId: stagedGoal.id,
      taskId: target.taskId,
      subTaskId: null,
      checklistId: movedItem.id,
    }
  } else if (target.kind === 'into-subtask') {
    stagedGoal = replaceContainerChecklist(stagedGoal, target.taskId, target.subTaskId, (current) => [
      ...current,
      movedItem,
    ])
    nextSelection = {
      kind: 'checklist',
      goalId: stagedGoal.id,
      taskId: target.taskId,
      subTaskId: target.subTaskId,
      checklistId: movedItem.id,
    }
  } else {
    // before-checklist — find the target's index in its container and insert before.
    stagedGoal = replaceContainerChecklist(stagedGoal, target.taskId, target.subTaskId, (current) => {
      const insertIndex = current.findIndex((item) => item.id === target.checklistId)
      if (insertIndex < 0) return [...current, movedItem]
      return [...current.slice(0, insertIndex), movedItem, ...current.slice(insertIndex)]
    })
    nextSelection = {
      kind: 'checklist',
      goalId: stagedGoal.id,
      taskId: target.taskId,
      subTaskId: target.subTaskId,
      checklistId: movedItem.id,
    }
  }

  return { next: stagedGoal, nextSelection }
}

/** All node ids in a subtree rooted at selection (inclusive). */
export const collectSubtreeIds = (selection: DetailedSelection, goal: Goal): string[] => {
  if (selection.kind === 'goal') {
    const ids: string[] = [goal.id]
    goal.tasks.forEach((task) => {
      ids.push(task.id)
      task.subTasks.forEach((sub) => ids.push(sub.id))
    })
    return ids
  }
  if (selection.kind === 'task') {
    const task = goal.tasks.find((candidate) => candidate.id === selection.taskId)
    if (!task) return [selection.taskId]
    return [selection.taskId, ...task.subTasks.map((sub) => sub.id)]
  }
  if (selection.kind === 'subtask') {
    return [selection.subTaskId]
  }
  // checklist: its own id only.
  return [selection.checklistId]
}

/** Every Goal/Task/Subtask id in the tree. */
export const collectAllGoalLikeIds = (goal: Goal): string[] => {
  const ids = [goal.id]
  goal.tasks.forEach((task) => {
    ids.push(task.id)
    task.subTasks.forEach((sub) => ids.push(sub.id))
  })
  return ids
}

/** Find parent path for a node id; used to decide if a child can save yet. */
export const findGoalLikeAncestors = (
  goal: Goal,
  nodeId: string,
): { kind: 'goal' | 'task' | 'subtask'; ancestors: string[] } | null => {
  if (goal.id === nodeId) return { kind: 'goal', ancestors: [] }
  for (const task of goal.tasks) {
    if (task.id === nodeId) return { kind: 'task', ancestors: [goal.id] }
    for (const sub of task.subTasks) {
      if (sub.id === nodeId) return { kind: 'subtask', ancestors: [goal.id, task.id] }
    }
  }
  return null
}

/** Drop a Task or Subtask from the goal tree by id. Used when discarding. */
export const removeNodeFromGoal = (goal: Goal, nodeId: string): Goal => {
  if (goal.id === nodeId) return goal // safety — caller decides to nullify the goal entirely
  return {
    ...goal,
    tasks: goal.tasks
      .filter((task) => task.id !== nodeId)
      .map((task) => ({
        ...task,
        subTasks: task.subTasks.filter((sub) => sub.id !== nodeId),
      })),
  }
}

/** Snapshot helper — deep-clone a Goal via JSON round trip. Used for baselines. */
export const cloneGoal = (goal: Goal): Goal => JSON.parse(JSON.stringify(goal)) as Goal

// Re-exported helpers so callers don't import deep paths.
export type { Task, SubTask, ChecklistItem }
